/* Neeta Lulla — custom homepage design system behaviour.
   Defensive: every block checks its elements exist before wiring up,
   since individual sections can be reordered, duplicated, or removed
   in the theme editor.

   This file is included via {% script_tag %} in several section templates
   (header, hero, films gallery, testimonials) so the behaviour still loads
   no matter which of those sections a merchant keeps on the page. Because
   of that, this same script can execute more than once, and — critically —
   each <script> tag runs synchronously at the point it's parsed, so code
   that runs immediately only ever sees the DOM markup that exists *so far*
   (sections further down the page, like testimonials/appointment/occasion/
   menswear, don't exist yet and get silently skipped). Guard against both:
   only run once globally, and always wait for the full DOM before querying
   it, so every section — wherever it sits in the page — gets wired up. */
(function(){
  if (window.__nlHomepageInit) return;
  window.__nlHomepageInit = true;

  function run(){

  // ---------- Fix mobile viewport-height jump (address-bar show/hide) ----------
  function setRealVh(){
    document.documentElement.style.setProperty('--nl-vh', (window.innerHeight * 0.01) + 'px');
  }
  setRealVh();
  window.addEventListener('resize', setRealVh);
  window.addEventListener('orientationchange', setRealVh);

  // ---------- Header: hide on scroll down, reveal on scroll up (every page) ----------
  // Site-wide because the header is rendered once, in header-group, on every template.
  // Reveal is immediate on the smallest genuine upward movement; hiding only kicks in once
  // the page has scrolled past the header's own height, so it never disappears near the top.
  (function(){
    var header = document.querySelector('header.main-header');
    if (!header) return;
    var topBar = document.querySelector('.top-bar');
    var docEl = document.documentElement;
    var lastY = Math.max(window.pageYOffset, 0);
    var ticking = false;
    // After an instant wheel/touch reveal, window.pageYOffset can take a moment to
    // actually catch up with the gesture. Until it does, a 'scroll' event can still
    // report a stale, larger y than expected and look like a fresh downward move —
    // which would hide the header again mid-way through its own reveal animation and
    // leave it out of step with the toolbar (a visible gap between them). Block hiding
    // for a short window after any instant reveal so that stale reading can't do this;
    // revealing again during that window is harmless, so only hiding is suppressed.
    var suppressHideUntil = 0;
    function isBusy(){
      return !!document.querySelector('.main-nav.mobile-open') || document.body.style.overflow === 'hidden';
    }
    function reveal(){ docEl.classList.remove('nl-header-hidden'); }
    function hide(){ docEl.classList.add('nl-header-hidden'); }
    function resync(){ lastY = Math.max(window.pageYOffset, 0); }
    function revealNow(){ reveal(); resync(); suppressHideUntil = Date.now() + 400; }

    // The header is position:fixed (not sticky) specifically to dodge a browser bug:
    // a position:sticky element that receives a transform can permanently lose its
    // "stuck" tracking once scrolled deep enough, and never recover — even after the
    // transform is removed — leaving it stranded off-screen while everything else
    // thinks it should be visible. Fixed positioning has no such stuck/unstuck state
    // to corrupt, so we recreate the old sticky effect ourselves: sit right below the
    // announcement bar, then pin to the very top once that bar has scrolled away.
    function positionHeader(){
      var tbH = topBar ? topBar.offsetHeight : 0;
      var y = Math.max(window.pageYOffset, 0);
      header.style.top = Math.max(0, tbH - y) + 'px';
    }
    function setHeaderSpace(){
      // Fixed elements reserve no space in normal flow, unlike the sticky element this
      // replaces — restore that reserved space so page content doesn't start underneath
      // the header. main.main-content reads this custom property for its top padding.
      docEl.style.setProperty('--nl-fixed-header-h', header.offsetHeight + 'px');
    }
    positionHeader();
    setHeaderSpace();
    window.addEventListener('resize', function(){ positionHeader(); setHeaderSpace(); });
    window.addEventListener('load', setHeaderSpace);

    function update(){
      ticking = false;
      positionHeader();
      var y = Math.max(window.pageYOffset, 0);
      var diff = y - lastY;
      if (isBusy() || y <= header.offsetHeight) { lastY = y; reveal(); return; }
      // Reveal on ANY genuine upward movement — no threshold — so the header comes back
      // the instant the person starts scrolling up, however small that first movement is.
      if (diff < -0.5) { lastY = y; reveal(); return; }
      if (Date.now() < suppressHideUntil) { lastY = y; return; }
      // Hiding needs a real downward movement, so ordinary jitter (trackpad noise, a
      // resize reflow) can't hide it — un-qualifying movement is left unaccounted so it
      // keeps accumulating against the old lastY until it either clears this or reverses.
      if (diff > 4) { lastY = y; hide(); }
    }
    window.addEventListener('scroll', function(){
      if (!ticking) { ticking = true; window.requestAnimationFrame(update); }
    }, { passive: true });

    // Belt-and-braces: read the input device directly, not just the resulting scroll
    // position. A mouse-wheel or trackpad "scroll up" tick reveals the header the
    // instant it happens — this doesn't wait on window.pageYOffset actually moving,
    // which can lag or behave inconsistently across browsers, custom scroll containers,
    // or momentum/inertia scrolling — so the header can never fail to come back.
    window.addEventListener('wheel', function(e){
      if (e.deltaY < 0 && !isBusy()) { revealNow(); }
    }, { passive: true });

    // Same idea for touch: the finger moving down the screen means the content is
    // moving down too, i.e. the person is scrolling back up through the page.
    var touchY = null;
    window.addEventListener('touchstart', function(e){
      touchY = (e.touches && e.touches[0]) ? e.touches[0].clientY : null;
    }, { passive: true });
    window.addEventListener('touchmove', function(e){
      if (touchY == null || !e.touches || !e.touches[0]) return;
      var y2 = e.touches[0].clientY;
      if (y2 - touchY > 5 && !isBusy()) { revealNow(); }
      touchY = y2;
    }, { passive: true });
  })();

  // ---------- Hero slider (supports multiple hero sections on one page) ----------
  document.querySelectorAll('.hero[id^="hero-"], .hero').forEach(function(heroEl){
    if (heroEl.dataset.nlHeroInit) return;
    heroEl.dataset.nlHeroInit = '1';
    var slides = heroEl.querySelectorAll('.hero-slide');
    var dotsWrap = heroEl.querySelector('.hero-dots');
    if (!slides.length) return;
    var heroIndex = 0, heroTimer;
    var dots = [];

    if (dotsWrap) {
      slides.forEach(function(_, i){
        var d = document.createElement('button');
        if (i === 0) d.classList.add('active');
        d.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        d.addEventListener('click', function(){ goHero(i); });
        dotsWrap.appendChild(d);
      });
      dots = dotsWrap.querySelectorAll('button');
    }

    function goHero(i){
      slides[heroIndex].classList.remove('active');
      if (dots[heroIndex]) dots[heroIndex].classList.remove('active');
      heroIndex = (i + slides.length) % slides.length;
      slides[heroIndex].classList.add('active');
      if (dots[heroIndex]) dots[heroIndex].classList.add('active');
      resetHeroTimer();
    }
    function resetHeroTimer(){
      clearInterval(heroTimer);
      if (slides.length < 2) return;
      var speed = parseInt(heroEl.getAttribute('data-autoplay-speed'), 10) || 6000;
      heroTimer = setInterval(function(){ goHero(heroIndex + 1); }, speed);
    }
    var prevBtn = heroEl.querySelector('.hero-nav.prev');
    var nextBtn = heroEl.querySelector('.hero-nav.next');
    if (prevBtn) prevBtn.addEventListener('click', function(){ goHero(heroIndex - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function(){ goHero(heroIndex + 1); });
    if (heroEl.getAttribute('data-autoplay') !== 'false') resetHeroTimer();
  });

  // ---------- Testimonial / quote "Read more" toggle ----------
  document.querySelectorAll('.read-more-toggle').forEach(function(btn){
    var quote = btn.previousElementSibling;
    if (!quote || !quote.classList.contains('quote')) return;
    btn.addEventListener('click', function(){
      var expanded = quote.classList.toggle('clamped') === false;
      btn.textContent = expanded ? (btn.dataset.lessLabel || 'Read less') : (btn.dataset.moreLabel || 'Read more');
    });
  });

  // ---------- Films / marquee rows: pause on touch so it doesn't fight the finger ----------
  document.querySelectorAll('.films-row-outer').forEach(function(outer){
    var row = outer.querySelector('.films-row');
    if (!row) return;
    outer.addEventListener('touchstart', function(){ row.style.animationPlayState = 'paused'; }, { passive:true });
    outer.addEventListener('touchend', function(){ row.style.animationPlayState = 'running'; }, { passive:true });
    outer.addEventListener('touchcancel', function(){ row.style.animationPlayState = 'running'; }, { passive:true });
  });

  // ---------- Top announcement bar: rotate one line at a time on mobile ----------
  document.querySelectorAll('.top-bar').forEach(function(bar){
    var items = Array.prototype.slice.call(bar.querySelectorAll('.top-bar-item'));
    if (items.length < 2) return;
    var idx = 0;
    setInterval(function(){
      if (window.innerWidth > 640) return;
      items[idx].classList.remove('active');
      idx = (idx + 1) % items.length;
      items[idx].classList.add('active');
    }, 3200);
  });

  // ---------- Mobile menu ----------
  document.querySelectorAll('.hamburger').forEach(function(btn){
    var nav = document.querySelector('.main-nav');
    if (!nav) return;
    btn.addEventListener('click', function(){
      nav.classList.toggle('mobile-open');
    });
  });

  // ---------- Scroll reveal ----------
  if ('IntersectionObserver' in window) {
    var io = new IntersectionObserver(function(entries){
      entries.forEach(function(e){
        if (e.isIntersecting){
          e.target.classList.add('in');
          e.target.querySelectorAll('video[autoplay]').forEach(function(v){
            var p = v.play();
            if (p && p.catch) p.catch(function(){});
          });
        }
      });
    }, { threshold: 0.12 });
    document.querySelectorAll('[data-reveal]').forEach(function(el){ io.observe(el); });

    // Belt-and-braces: nudge every autoplay video to (re)play whenever it
    // scrolls into view, so mobile browsers that suspended it while hidden resume it.
    var videoIO = new IntersectionObserver(function(entries){
      entries.forEach(function(e){
        if (e.isIntersecting){
          var p = e.target.play();
          if (p && p.catch) p.catch(function(){});
        }
      });
    }, { threshold: 0.2 });
    document.querySelectorAll('video[autoplay]').forEach(function(v){ videoIO.observe(v); });
  } else {
    document.querySelectorAll('[data-reveal]').forEach(function(el){ el.classList.add('in'); });
  }

  // ---------- Card-row slider arrows (testimonials, etc.) ----------
  document.querySelectorAll('[data-nl-slider]').forEach(function(wrap){
    var row = wrap.querySelector('[data-nl-slider-row]');
    var prev = wrap.querySelector('[data-nl-slider-prev]');
    var next = wrap.querySelector('[data-nl-slider-next]');
    if (!row || !prev || !next) return;
    var step = function(){
      var card = row.firstElementChild;
      if (!card) return row.clientWidth;
      var gap = parseFloat(getComputedStyle(row).gap) || 24;
      return card.getBoundingClientRect().width + gap;
    };
    next.addEventListener('click', function(){ row.scrollBy({ left: step(), behavior: 'smooth' }); });
    prev.addEventListener('click', function(){ row.scrollBy({ left: -step(), behavior: 'smooth' }); });

    // If the cards don't fill the row's full width (e.g. only 3 cards where 4
    // would fit on desktop), there's nothing to scroll — center the cards
    // instead of leaving them stuck to the left with empty space on the
    // right, and hide the now-pointless prev/next arrows. Re-checked on
    // resize since the fitting card count changes across breakpoints.
    var arrowsWrap = wrap.querySelector('.testi-arrows') || wrap;
    function updateSliderFit(){
      var hasOverflow = row.scrollWidth > row.clientWidth + 1;
      row.classList.toggle('is-centered', !hasOverflow);
      if (arrowsWrap) arrowsWrap.style.display = hasOverflow ? '' : 'none';
    }
    updateSliderFit();
    window.addEventListener('resize', updateSliderFit);
    window.addEventListener('load', updateSliderFit);
  });

  } // end run()

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

})();